import {
  Autocomplete,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppRoutes } from '../../models'
import { AuthContext } from '../../providers'
import {
  useGetCustomersForVehicleQuery,
  useLinkCustomerToVehicleMutation
} from '../../redux/api'
import { CustomerEntry } from '../../sdk'
import { formatPhoneNumber } from '../../utils'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import { loggingAssert } from '../../utils/handleError/loggingAssert'
import { AddCustomerDialog } from '../Customers/AddCustomerDialog'
import { ClickableDeletableCard } from '../SharedUI'

interface Props {
  vehicleId: string
  customer: CustomerEntry | undefined
  setCustomer: (customer: CustomerEntry | undefined) => void
  readOnly?: boolean
  autoFocus?: boolean
}

export const CustomersByVehicleAutocomplete: React.FC<Props> = ({
  vehicleId,
  customer,
  setCustomer,
  readOnly,
  autoFocus = false
}) => {
  const { currentUser, userShopId } = useContext(AuthContext)
  const accessToken = loggingAssert('Token not set', currentUser?.accessToken)
  const shopId = loggingAssert('Missing shop id.', userShopId)
  const navigate = useNavigate()

  const { data: customers, isLoading } = useGetCustomersForVehicleQuery({
    authorization: accessToken,
    shop: shopId,
    vehicle: vehicleId
  })
  useEffect(() => {
    if (customers && customers.length === 1) {
      setCustomer(customers[0])
    }
  }, [setCustomer, customers])

  const handleChange = (
    event: React.SyntheticEvent,
    value: CustomerEntry | null
  ) => {
    const data = (customers || []).filter(
      (customer) => customer.id === value?.id
    )[0]
    setCustomer(data)
  }

  const handleCancel = useCallback(() => {
    setCustomer(undefined)
  }, [setCustomer])

  const [openAddCustomerDialog, setOpenAddCustomerDialog] =
    useState<boolean>(false)

  const [linkCustomerToVehicle] = useLinkCustomerToVehicleMutation()

  const handleAddCustomer = useCallback(
    async (customer: CustomerEntry) => {
      await linkCustomerToVehicle({
        vehicle: vehicleId,
        shop: shopId,
        customer: customer.id!,
        authorization: accessToken
      })
    },
    [accessToken, linkCustomerToVehicle, shopId, vehicleId]
  )

  const renderList = (
    _props: React.HTMLAttributes<HTMLLIElement>,
    option: CustomerEntry
  ) =>
    option.id !== '*' ? (
      <ListItem sx={{ padding: 0 }} {..._props} key={option.id}>
        <ListItemText
          primary={
            <Typography sx={{ fontWeight: 'bold' }}>
              {formatName(option.information)}
            </Typography>
          }
          secondary={
            <>
              {option.information?.email && (
                <Typography>Email: {option.information?.email}</Typography>
              )}
              {option.information?.phone && (
                <Typography>
                  Phone: {formatPhoneNumber(option.information?.phone)}
                </Typography>
              )}
            </>
          }
        />
      </ListItem>
    ) : (
      <ListItem
        alignItems="center"
        key={option.id}
        disableGutters
        disablePadding>
        <ListItemButton onClick={() => setOpenAddCustomerDialog(true)}>
          <ListItemText
            primary="Create new customer"
            primaryTypographyProps={{
              variant: 'button',
              textAlign: 'center',
              color: 'primary'
            }}
          />
        </ListItemButton>
      </ListItem>
    )

  return (
    <>
      <AddCustomerDialog
        open={openAddCustomerDialog}
        setOpen={setOpenAddCustomerDialog}
        handleCustomerChange={handleAddCustomer}
        customerName={''}
      />

      {!customer && (
        <Autocomplete
          id="customer-selection"
          getOptionLabel={(option) => {
            return formatName(option.information)
          }}
          autoHighlight
          onChange={handleChange}
          options={[{ id: '*', information: {} }, ...(customers || [])]}
          autoComplete
          openOnFocus
          loading={isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search customer by name"
              fullWidth
              autoFocus={autoFocus}
            />
          )}
          renderOption={renderList}
          noOptionsText={'No options found'}
        />
      )}
      {customer && (
        <ClickableDeletableCard
          label={
            <Stack>
              <Typography variant="h6">
                {formatName(customer.information)}
              </Typography>
            </Stack>
          }
          onDelete={readOnly ? undefined : handleCancel}
          onClick={() => navigate(AppRoutes.CUSTOMERS + '/' + customer.id)}
        />
      )}
    </>
  )
}
